import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch } from "./store";

/* ═══════════════════════════════════════════════════════════
   STORE PRODUCT RATING SLICE
   Namespaced ("storeProductRatings") ratings/reviews for store
   products. Writes to "storeProductRatings/{listingId}".
   Twin of the original product ratings, plus the logic the
   store side was missing:
     • mirrors the rolling summary onto the StoreProducts doc
       (ratingAvg / ratingCount) so StoreProducts.tsx and the
       marketplace card can show stars without a second read
     • a real-time summaries listener for the home feed
     • per-listing selectors used by StoreProductsHome cards
   StoreProductsHome reads `state.storeProductRatings.summaries`,
   which is exactly what fetchAllProductSummaries fills.
═══════════════════════════════════════════════════════════ */

const RATINGS_COLLECTION = "storeProductRatings";
const PRODUCTS_COLLECTION = "StoreProducts";

/* ──────────────────────────
   TYPES
────────────────────────── */

export interface StoreProductReview {
  images: boolean;
  title: any;
  guestId: string;
  orderId: string;

  rating: number;
  reviewText: string;

  quality: number;
  deliverySpeed: number;
  accuracy: number;
  communication: number;
  value: number;

  trueToSize?: boolean;
  damaged?: boolean;
  wouldRecommend?: boolean;

  dateCreated: string;
}

export interface StoreProductRatingSummary {
  avgRating: number;
  quality: number;
  deliverySpeed: number;
  accuracy: number;
  communication: number;
  value: number;

  recommendPercentage: number;
  damageReports: number;

  totalReviews: number;
  lastUpdated: string;
}

interface StoreProductRatingState {
  reviews: Record<string, StoreProductReview[]>;
  summaries: Record<string, StoreProductRatingSummary | null>;
  loading: boolean;
  error: string | null;
  summariesUnsub?: (() => void) | null;
}

const initialState: StoreProductRatingState = {
  reviews: {},
  summaries: {},
  loading: false,
  error: null,
  summariesUnsub: null,
};

/* ──────────────────────────
   HELPERS
────────────────────────── */

const emptySummary = (): StoreProductRatingSummary => ({
  avgRating: 0,
  quality: 0,
  deliverySpeed: 0,
  accuracy: 0,
  communication: 0,
  value: 0,
  recommendPercentage: 0,
  damageReports: 0,
  totalReviews: 0,
  lastUpdated: new Date().toISOString(),
});

/* Mirror the headline numbers onto the product doc so product
   lists can render stars without opening the ratings doc. Best
   effort — a failure here must never block the review write. */
const mirrorSummaryToProduct = async (
  listingId: string,
  summary: StoreProductRatingSummary
) => {
  try {
    const prodRef = doc(db, PRODUCTS_COLLECTION, listingId);
    await updateDoc(prodRef, {
      ratingAvg: parseFloat(summary.avgRating.toFixed(2)),
      ratingCount: summary.totalReviews,
      recommendPercentage: parseFloat(summary.recommendPercentage.toFixed(1)),
    });
  } catch {
    /* product doc may not exist / no permission — ignore */
  }
};

/* ──────────────────────────
   SUBMIT PRODUCT REVIEW
────────────────────────── */

export const submitStoreProductReview = createAsyncThunk(
  "storeProductRatings/submitReview",
  async (
    {
      listingId,
      hostId,
      guestId,
      orderId,
      rating,
      reviewText,
      criteria,
      trueToSize,
      damaged,
      wouldRecommend,
    }: {
      listingId: string;
      hostId: string;
      guestId: string;
      orderId: string;
      rating: number;
      reviewText: string;
      criteria: {
        quality: number;
        deliverySpeed: number;
        accuracy: number;
        communication: number;
        value: number;
      };
      trueToSize?: boolean;
      damaged?: boolean;
      wouldRecommend?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const guestRef = doc(db, RATINGS_COLLECTION, listingId, "reviews", guestId);
      const snap = await getDoc(guestRef);

      const newReview: StoreProductReview = {
        guestId,
        orderId,
        rating,
        reviewText,
        quality: criteria.quality,
        deliverySpeed: criteria.deliverySpeed,
        accuracy: criteria.accuracy,
        communication: criteria.communication,
        value: criteria.value,
        trueToSize,
        damaged,
        wouldRecommend,
        dateCreated: new Date().toISOString(),
        images: false,
        title: undefined,
      };

      /* prevent duplicate review per order */
      if (snap.exists()) {
        const existing = snap.data();
        const reviews = existing.reviews || [];
        const alreadyReviewed = reviews.some(
          (r: any) => r.orderId === orderId
        );
        if (alreadyReviewed) {
          return { listingId, reviewed: true };
        }
        await updateDoc(guestRef, {
          reviews: [...reviews, newReview],
          lastUpdated: Timestamp.now(),
        });
      } else {
        await setDoc(guestRef, {
          guestId,
          reviews: [newReview],
          createdAt: Timestamp.now(),
        });
      }

      /* ── update rolling summary ── */
      const listingRef = doc(db, RATINGS_COLLECTION, listingId);
      const listingSnap = await getDoc(listingRef);

      const summary: StoreProductRatingSummary =
        listingSnap.data()?.summary || emptySummary();

      const n = summary.totalReviews;
      const newN = n + 1;

      const updatedSummary: StoreProductRatingSummary = {
        avgRating: (summary.avgRating * n + rating) / newN,
        quality: (summary.quality * n + criteria.quality) / newN,
        deliverySpeed:
          (summary.deliverySpeed * n + criteria.deliverySpeed) / newN,
        accuracy: (summary.accuracy * n + criteria.accuracy) / newN,
        communication:
          (summary.communication * n + criteria.communication) / newN,
        value: (summary.value * n + criteria.value) / newN,
        recommendPercentage:
          (summary.recommendPercentage * n + (wouldRecommend ? 100 : 0)) / newN,
        damageReports: summary.damageReports + (damaged ? 1 : 0),
        totalReviews: newN,
        lastUpdated: new Date().toISOString(),
      };

      if (listingSnap.exists()) {
        await updateDoc(listingRef, { summary: updatedSummary });
      } else {
        await setDoc(listingRef, {
          listingId,
          hostId,
          summary: updatedSummary,
          createdAt: Timestamp.now(),
        });
      }

      /* NEW: keep the product doc's headline rating in sync */
      await mirrorSummaryToProduct(listingId, updatedSummary);

      return { listingId, review: newReview, summary: updatedSummary };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   FETCH REVIEWS BY LISTING
────────────────────────── */

export const fetchStoreProductReviewsByListing = createAsyncThunk(
  "storeProductRatings/fetchByListing",
  async (listingId: string, { rejectWithValue }) => {
    try {
      const colRef = collection(db, RATINGS_COLLECTION, listingId, "reviews");
      const snap = await getDocs(colRef);

      const allReviews: StoreProductReview[] = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.reviews) allReviews.push(...data.reviews);
      });

      /* newest first — nicer default for a reviews panel */
      allReviews.sort(
        (a, b) =>
          new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
      );

      return { listingId, reviews: allReviews };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   FETCH SINGLE REVIEW BY ORDER
────────────────────────── */

export const fetchStoreProductReviewByOrder = createAsyncThunk<
  StoreProductReview | null,
  { listingId: string; orderId: string; guestId: string },
  { rejectValue: string }
>(
  "storeProductRatings/fetchByOrder",
  async ({ listingId, orderId, guestId }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, RATINGS_COLLECTION, listingId, "reviews", guestId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;

      const data = snap.data();
      const reviews: StoreProductReview[] = data.reviews || [];
      return reviews.find((r) => r.orderId === orderId) ?? null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   FETCH SINGLE SUMMARY
────────────────────────── */

export const fetchStoreProductSummary = createAsyncThunk<
  { listingId: string; summary: StoreProductRatingSummary | null },
  string,
  { rejectValue: string }
>("storeProductRatings/fetchSummary", async (listingId, { rejectWithValue }) => {
  try {
    const snap = await getDoc(doc(db, RATINGS_COLLECTION, listingId));
    return { listingId, summary: snap.data()?.summary || null };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

/* ──────────────────────────
   FETCH ALL SUMMARIES
   (one-shot — used on home mount)
────────────────────────── */

export const fetchAllProductSummaries = createAsyncThunk(
  "storeProductRatings/fetchAllSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, RATINGS_COLLECTION));
      const summaries: Record<string, StoreProductRatingSummary | null> = {};
      snap.forEach((docSnap) => {
        summaries[docSnap.id] = docSnap.data().summary || null;
      });
      return summaries;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ──────────────────────────
   REAL-TIME: ALL SUMMARIES
   Keeps home-feed stars live as reviews land. Stores the unsub
   in state so the caller can stop it on unmount.
────────────────────────── */

export const listenToAllProductSummaries =
  () => (dispatch: AppDispatch, getState: () => any) => {
    const prev = getState().storeProductRatings?.summariesUnsub;
    if (prev) prev();

    const unsub = onSnapshot(collection(db, RATINGS_COLLECTION), (snap) => {
      const summaries: Record<string, StoreProductRatingSummary | null> = {};
      snap.forEach((docSnap) => {
        summaries[docSnap.id] = docSnap.data().summary || null;
      });
      dispatch(setAllSummaries(summaries));
    });

    dispatch(setSummariesUnsub(unsub));
    return unsub;
  };

export const stopProductSummariesListener =
  () => (dispatch: AppDispatch, getState: () => any) => {
    const unsub = getState().storeProductRatings?.summariesUnsub;
    if (unsub) {
      unsub();
      dispatch(setSummariesUnsub(null));
    }
  };

/* ──────────────────────────
   SLICE
────────────────────────── */

const storeProductRatingSlice = createSlice({
  name: "storeProductRatings",
  initialState,
  reducers: {
    setAllSummaries: (
      state,
      action: PayloadAction<Record<string, StoreProductRatingSummary | null>>
    ) => {
      state.summaries = action.payload;
    },
    setSummariesUnsub: (
      state,
      action: PayloadAction<(() => void) | null>
    ) => {
      state.summariesUnsub = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /* submit */
      .addCase(submitStoreProductReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitStoreProductReview.fulfilled, (state, action) => {
        if ((action.payload as any).review) {
          const { listingId, review, summary } = action.payload as any;
          if (!state.reviews[listingId]) state.reviews[listingId] = [];
          state.reviews[listingId].push(review);
          state.summaries[listingId] = summary;
        }
        state.loading = false;
      })
      .addCase(submitStoreProductReview.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      /* fetch reviews */
      .addCase(fetchStoreProductReviewsByListing.fulfilled, (state, action) => {
        state.reviews[action.payload.listingId] = action.payload.reviews;
      })

      /* fetch single summary */
      .addCase(fetchStoreProductSummary.fulfilled, (state, action) => {
        state.summaries[action.payload.listingId] = action.payload.summary;
      })

      /* fetch all summaries */
      .addCase(fetchAllProductSummaries.fulfilled, (state, action) => {
        state.summaries = action.payload;
      });
  },
});

export const { setAllSummaries, setSummariesUnsub } =
  storeProductRatingSlice.actions;

/* ──────────────────────────
   SELECTORS
────────────────────────── */

export const selectStoreSummary =
  (listingId: string) =>
  (s: any): StoreProductRatingSummary | null =>
    s.storeProductRatings?.summaries?.[listingId] ?? null;

export const selectStoreReviews =
  (listingId: string) =>
  (s: any): StoreProductReview[] =>
    s.storeProductRatings?.reviews?.[listingId] ?? [];

export default storeProductRatingSlice.reducer;
